import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FileText, Users, Building, BookOpen, Folder, UserCheck, Briefcase, MapPin, User, Search, TrendingUp } from 'lucide-react';
import TabNavigation from './TabNavigation';
import SearchHeader from './SearchHeader';
import ContentRenderer from './ContentRenderer';
import LoadingSpinner from './ui/LoadingSpinner';
import ErrorMessage from './ui/ErrorMessage';
import EmptyState from './ui/EmptyState';

const SearchResults = ({ searchQuery }) => {
    const [activeTab, setActiveTab] = useState('');
    const [contentData, setContentData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchData, setSearchData] = useState(null);
    const [error, setError] = useState(null);
    const [searchStartTime, setSearchStartTime] = useState(null);
    const [selectedIdEtp, setSelectedIdEtp] = useState(null);

    // Configuration constants
    const API_CONFIG = useMemo(() => ({
        BASE_URL: 'https://search.mg.formafusion.io',
        SEARCH_ENDPOINT: '/api/search-generality',
        CUSTOMER_ID: localStorage.getItem('idCustomer'),
        TIMEOUT: 100000
    }), []);

    // Icon mapping configuration
    const ICON_MAP = useMemo(() => ({
        learner: <Users className="w-4 h-4" />,
        entreprise: <Building className="w-4 h-4" />,
        course: <BookOpen className="w-4 h-4" />,
        folder: <Folder className="w-4 h-4" />,
        trainer: <UserCheck className="w-4 h-4" />,
        project: <Briefcase className="w-4 h-4" />,
        projectNeighborhood: <MapPin className="w-4 h-4" />,
        project_reference: <FileText className="w-4 h-4" />,
        projectCity: <MapPin className="w-4 h-4" />,
        projectPlace: <MapPin className="w-4 h-4" />,
        referent: <User className="w-4 h-4" />,
        referent_customer: <User className="w-4 h-4" />
    }), []);

    // Label mapping configuration
    const LABEL_MAP = useMemo(() => ({
        learner: 'Apprenants',
        entreprise: 'Clients',
        course: 'Cours',
        folder: 'Dossiers',
        trainer: 'Formateurs',
        project: 'Projets',
        projectNeighborhood: 'Projets par quartier',
        project_reference: 'Projets par référence',
        projectCity: 'Projets par ville',
        projectPlace: 'Projets par lieu',
        referent: 'Référents',
        referent_customer: 'Référents clients'
    }), []);

    // Helper functions
    const getIcon = useCallback((type) => {
        return ICON_MAP[type] || <FileText className="w-4 h-4" />;
    }, [ICON_MAP]);

    const getTabLabel = useCallback((type) => {
        return LABEL_MAP[type] || type;
    }, [LABEL_MAP]);

    // Find first available tab with priority
    const findFirstAvailableTab = useCallback((data) => {

        // Priority order for tabs
        const tabPriority = ['project', 'learner', 'entreprise', 'trainer', 'course', 'referent'];

        // Check priority tabs first
        for (const key of tabPriority) {
            if (data[key] && data[key].count > 0) {
                return {
                    tabId: key,
                    route: data[key].route,
                    idEtp: null // Regular tabs don't have idEtp
                };
            }
        }

        // Check remaining regular tabs
        for (const [key, value] of Object.entries(data)) {
            if (key !== 'project_with_client' && !tabPriority.includes(key) && value.count > 0) {
                return {
                    tabId: key,
                    route: value.route,
                    idEtp: null // Regular tabs don't have idEtp
                };
            }
        }

        // Check project_with_client tabs
        if (data.project_with_client && Array.isArray(data.project_with_client)) {
            for (let i = 0; i < data.project_with_client.length; i++) {
                const project = data.project_with_client[i];
                if (project.count > 0) {
                    return {
                        tabId: project.type,
                        route: project.route,
                        idEtp: project.idEtp // This should have the actual idEtp value
                    };
                }
            }
        }

        return null;
    }, []);

    // Enhanced fetch with timeout and retry logic
    const fetchWithTimeout = useCallback(async (url, options = {}, timeout = API_CONFIG.TIMEOUT) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        try {
            const response = await fetch(url, {
                ...options,
                signal: controller.signal
            });
            clearTimeout(timeoutId);
            return response;
        } catch (error) {
            clearTimeout(timeoutId);
            throw error;
        }
    }, [API_CONFIG.TIMEOUT]);

    // Load tab content with enhanced error handling - FIXED to accept and preserve idEtp
    const loadTabContent = useCallback(async (route, idEtp = null, tabType = null) => {
        setLoading(true);
        setError(null);

        try {
            // Set the idEtp state immediately when loading content
            setSelectedIdEtp(idEtp);

            // Create content data with proper idEtp preservation
            const mockData = {
                items: [],
                route: route,
                idEtp: idEtp, // Preserve idEtp in contentData
                tabType: tabType, // Add tabType for debugging
                timestamp: Date.now()
            };

            setContentData(mockData);

        } catch (error) {
            console.error('Error loading content:', error);
            setError('Erreur lors du chargement du contenu');
            setContentData({
                items: [],
                route: null,
                idEtp: null,
                tabType: null
            });
            setSelectedIdEtp(null);
        } finally {
            setLoading(false);
        }
    }, []);

    // Enhanced search with better error handling
    const fetchSearchResults = useCallback(async () => {
        if (!searchQuery?.trim()) return;

        setLoading(true);
        setError(null);
        setSearchStartTime(Date.now());

        try {
            const url = `${API_CONFIG.BASE_URL}${API_CONFIG.SEARCH_ENDPOINT}?key=${encodeURIComponent(searchQuery.trim())}&idCustomer=${API_CONFIG.CUSTOMER_ID}`;

            const response = await fetchWithTimeout(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
            });

            if (!response.ok) {
                throw new Error(`Erreur ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            // Validate response structure
            if (!data || typeof data !== 'object') {
                throw new Error('Format de réponse invalide');
            }
            setSearchData(data);

            // Auto-select first available tab
            const firstTab = findFirstAvailableTab(data);
            if (firstTab) {
                setActiveTab(firstTab.tabId);
                // Load content with the correct idEtp - FIXED
                await loadTabContent(firstTab.route, firstTab.idEtp, firstTab.tabId);
            } else {
                setActiveTab('');
                setContentData(null);
                setSelectedIdEtp(null);
            }

        } catch (error) {
            console.error('Search error:', error);
            let errorMessage = 'Erreur lors de la recherche';

            if (error.name === 'AbortError') {
                errorMessage = 'La recherche a pris trop de temps';
            } else if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
                errorMessage = 'Problème de connexion réseau';
            } else if (error.message.includes('404')) {
                errorMessage = 'Service de recherche non disponible';
            } else if (error.message.includes('500')) {
                errorMessage = 'Erreur serveur, veuillez réessayer';
            }

            setError(errorMessage);
            setSelectedIdEtp(null);
        } finally {
            setLoading(false);
        }
    }, [searchQuery, API_CONFIG, fetchWithTimeout, findFirstAvailableTab, loadTabContent]);

    // Handle tab click with loading state - FIXED to pass idEtp to loadTabContent
    const handleTabClick = useCallback(async (tabId, route, idEtp = null) => {
        // Don't prevent re-clicking the same tab if idEtp is different
        if (activeTab === tabId && selectedIdEtp === idEtp) return;

        setActiveTab(tabId);
        setContentData(null); // Clear content immediately for better UX

        // Load content with the correct idEtp - FIXED
        await loadTabContent(route, idEtp, tabId);
    }, [activeTab, selectedIdEtp, loadTabContent]);

    // Retry function - FIXED to maintain current idEtp
    const handleRetry = useCallback(() => {
        setError(null);
        if (contentData?.route) {
            loadTabContent(contentData.route, selectedIdEtp, contentData.tabType);
        } else {
            fetchSearchResults();
        }
    }, [contentData?.route, contentData?.tabType, selectedIdEtp, loadTabContent, fetchSearchResults]);

    // Effect to fetch search results when query changes
    useEffect(() => {
        if (searchQuery?.trim()) {
            fetchSearchResults();
        } else {
            setSearchData(null);
            setContentData(null);
            setActiveTab('');
            setSelectedIdEtp(null);
            setError(null);
        }
    }, [searchQuery, fetchSearchResults]);

    // Calculate search metrics
    const searchMetrics = useMemo(() => {
        if (!searchData) return null;

        const totalResults = Object.values(searchData).reduce((total, item) => {
            if (Array.isArray(item)) {
                return total + item.reduce((sum, subItem) => sum + (subItem.count || 0), 0);
            }
            return total + (item.count || 0);
        }, 0);

        const duration = searchStartTime ? Date.now() - searchStartTime : 0;

        return {
            totalResults,
            duration: duration / 1000,
            categories: Object.keys(searchData).filter(key =>
                searchData[key].count > 0 || (Array.isArray(searchData[key]) && searchData[key].some(item => item.count > 0))
            ).length
        };
    }, [searchData, searchStartTime]);

    // Debug logging - can be removed in production
    // useEffect(() => {
    //     console.log('🔍 Current state update:', {
    //         activeTab,
    //         selectedIdEtp,
    //         selectedIdEtpType: typeof selectedIdEtp,
    //         contentDataIdEtp: contentData?.idEtp,
    //         contentDataIdEtpType: typeof contentData?.idEtp,
    //         contentDataExists: !!contentData,
    //         searchDataExists: !!searchData
    //     });
    // }, [activeTab, selectedIdEtp, contentData]);

    // Loading state for initial search
    if (!searchData && loading) {
        return (
            <div className="w-full min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
                <div className="container px-4 py-8 mx-auto">
                    <div className="flex items-center justify-center h-64">
                        <LoadingSpinner message="Recherche en cours..." size="large" />
                    </div>
                </div>
            </div>
        );
    }

    // Error state for search
    if (error && !searchData) {
        return (
            <div className="w-full min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
                <div className="container px-4 py-8 mx-auto">
                    <ErrorMessage
                        title="Erreur de recherche"
                        message={error}
                        onRetry={handleRetry}
                        className="h-64"
                    />
                </div>
            </div>
        );
    }

    // No search data
    if (!searchData) {
        return (
            <div className="w-full min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
                <div className="container px-4 py-8 mx-auto">
                    <EmptyState
                        icon={<Search className="w-16 h-16" />}
                        title="Commencez votre recherche"
                        description="Saisissez des termes de recherche pour trouver des résultats"
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="w-full min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
            <div className="container px-4 py-8 mx-auto max-w-7xl">
                {/* Enhanced Header */}
                <SearchHeader
                    searchQuery={searchQuery}
                    metrics={searchMetrics}
                    error={error}
                />

                {/* Tab Navigation */}
                <TabNavigation
                    searchData={searchData}
                    activeTab={activeTab}
                    onTabClick={handleTabClick}
                    getIcon={getIcon}
                    getTabLabel={getTabLabel}
                />

                {/* Main Content */}
                <div className="p-8 bg-white shadow-xl rounded-2xl backdrop-blur-sm bg-white/95">
                    {loading ? (
                        <div className="flex items-center justify-center h-48">
                            <LoadingSpinner message="Chargement du contenu..." />
                        </div>
                    ) : contentData ? (
                        <ContentRenderer
                            activeTab={activeTab}
                            contentData={{
                                ...contentData,
                                idEtp: selectedIdEtp // Ensure idEtp is always passed
                            }}
                            searchQuery={searchQuery}
                            getTabLabel={getTabLabel}
                            customerId={API_CONFIG.CUSTOMER_ID}
                            idEtp={selectedIdEtp}
                        />
                    ) : activeTab ? (
                        <ErrorMessage
                            title="Contenu non disponible"
                            message="Impossible de charger le contenu pour cet onglet"
                            onRetry={() => loadTabContent(contentData?.route, selectedIdEtp, contentData?.tabType)}
                            className="h-48"
                        />
                    ) : (
                        <EmptyState
                            icon={<TrendingUp className="w-12 h-12" />}
                            title="Sélectionnez une catégorie"
                            description="Choisissez un onglet ci-dessus pour explorer les résultats"
                            className="h-48"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default SearchResults;